import getFocusableElements from '../../../static/js/lib/get-focusable-elements.js';

const {registerModule} = window;

class NavigationBar {
  constructor(element) {
    this.element = element;
    this.initialize();
  }

  bindEvents() {
    if (this.isStickyNav) {
      this.bindStickySubnavEvents();
      this.handleStickySubnavNavigationScroll =
        this.handleStickySubnavNavigationScroll.bind(this);
      this.ui.$navigation.addEventListener(
        'scroll',
        this.handleStickySubnavNavigationScroll,
      );
    } else if (!this.isStickyNav) {
      this.ui.$next.addEventListener('click', this.handleClickNext.bind(this));
      this.ui.$prev.addEventListener('click', this.handleClickPrev.bind(this));
      if (this.ui.$searchButton) {
        this.ui.$searchButton.addEventListener(
          'click',
          this.handleOpenSearch.bind(this),
        );
        this.ui.$searchButton.addEventListener(
          'click',
          this.handleOpenSearch.bind(this),
        );
      }
    }
    window.addEventListener(
      'DOMContentLoaded',
      this.handleDomLoaded.bind(this),
    );
  }

  bindExternalUI() {
    this.externalUi = {};
    this.externalUi.$searchModal = document.querySelector(
      `[data-modal-id="${this.modalId}"]`,
    );
  }

  bindIntersectionObservers() {
    if (this.isStickyNav) {
      this.bindStickySubnavVisibilityObserver();
      return;
    }

    const options = {
      root: this.ui.$wrapper,
      threshold: 0.9,
      rootMargin: '200px 0px 200px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.dataset.isVisible = entry.isIntersecting;
      });

      const visibleItems = this.ui.$listItems.filter(
        ($listItem) => $listItem.dataset.isVisible === 'true',
      );

      const indexPrevious = this.ui.$listItems.indexOf(visibleItems[0]) - 1;
      const indexNext =
        this.ui.$listItems.indexOf(visibleItems[visibleItems.length - 1]) + 1;

      this.previousItem =
        indexPrevious >= 0 ? this.ui.$listItems[indexPrevious] : undefined;
      this.nextItem =
        indexNext >= 0 ? this.ui.$listItems[indexNext] : undefined;

      this.updateUI();
    }, options);

    this.ui.$listItems.forEach(($listItem) => {
      observer.observe($listItem);
    });
  }

  bindStickySubnavEvents() {
    this.onStickySubnavClick = this.onStickySubnavClick.bind(this);
    this.onStickySubnavKeydown = this.onStickySubnavKeydown.bind(this);
    this.onStickySubnavDocumentClick =
      this.onStickySubnavDocumentClick.bind(this);
    this.onStickySubnavResize = this.onStickySubnavResize.bind(this);
    this.onStickySubnavMouseLeave = this.onStickySubnavMouseLeave.bind(this);
    this.onStickySubnavFocusIn = this.onStickySubnavFocusIn.bind(this);
    this.stickySubnavDropdownHomes = new WeakMap();

    if (window.matchMedia('(hover: hover)').matches) {
      this.ui.$dropdownListItems.forEach(($listItem) => {
        $listItem.addEventListener('mouseenter', () => {
          if (!this.isListItemFullyVisible($listItem)) {
            return;
          }

          const button = $listItem.querySelector('[data-ref="sticky-subnav-btn"]');

          if (button) {
            this.openStickySubnavDropdown(button.getAttribute('aria-controls'));
          }
        });

        $listItem.addEventListener('mouseleave', (event) => {
          const openDropdown = this.getOpenStickySubnavDropdown();

          if (openDropdown?.contains(event.relatedTarget)) {
            return;
          }

          if (!event.relatedTarget || !$listItem.contains(event.relatedTarget)) {
            this.closeStickySubnavDropdowns();
          }
        });
      });

      this.element.addEventListener('mouseleave', this.onStickySubnavMouseLeave);
    }

    this.element.addEventListener('click', this.onStickySubnavClick);
    this.element.addEventListener('keydown', this.onStickySubnavKeydown);
    this.ui.$listItems.forEach(($listItem) => {
      $listItem.addEventListener('focusin', this.onStickySubnavFocusIn);
    });
    document.addEventListener('click', this.onStickySubnavDocumentClick);
    window.addEventListener('resize', this.onStickySubnavResize);

    this.closeStickySubnavDropdowns();
  }

  bindStickySubnavVisibilityObserver() {
    this.updateStickySubnavItemVisibility =
      this.updateStickySubnavItemVisibility.bind(this);

    requestAnimationFrame(() => {
      this.updateStickySubnavItemVisibility();
    });
  }

  getStickySubnavVisibilityTarget($listItem) {
    return (
      $listItem.querySelector('[data-ref="sticky-subnav-btn"]') ||
      $listItem.querySelector(':scope > a')
    );
  }

  isStickySubnavOpenDropdownFocusTarget(element) {
    const openDropdown = element?.closest(
      '[data-ref="sticky-subnav-dropdown"]:not([hidden])',
    );

    return Boolean(openDropdown);
  }

  shouldBlurClippedStickySubnavFocus(listItem, target) {
    if (this.isStickySubnavOpenDropdownFocusTarget(target)) {
      return false;
    }

    return !this.isListItemFullyVisible(listItem);
  }

  updateStickySubnavItemVisibility() {
    const navRect = this.ui.$navigation.getBoundingClientRect();
    const scrollportLeft = navRect.left;
    const scrollportRight = this.ui.$featuredNavigationItem ?
      Math.min(
        navRect.right,
        this.ui.$featuredNavigationItem.getBoundingClientRect().left,
      ) :
      navRect.right;

    this.ui.$listItems.forEach(($listItem) => {
      const target = this.getStickySubnavVisibilityTarget($listItem);

      if (!target) {
        return;
      }

      const targetRect = target.getBoundingClientRect();
      const isFullyVisible =
        targetRect.width > 0 &&
        targetRect.height > 0 &&
        targetRect.left >= scrollportLeft - 1 &&
        targetRect.right <= scrollportRight + 1 &&
        targetRect.top >= navRect.top - 1 &&
        targetRect.bottom <= navRect.bottom + 1;
      const wasFullyVisible = $listItem.dataset.isFullyVisible === 'true';

      $listItem.dataset.isFullyVisible = isFullyVisible;

      if (!isFullyVisible && wasFullyVisible) {
        const focusedElement = $listItem.querySelector(':focus');

        if (
          focusedElement &&
          this.shouldBlurClippedStickySubnavFocus($listItem, focusedElement)
        ) {
          focusedElement.blur();
        }
      }
    });
  }

  bindUI() {
    this.ui = {};
    this.ui.$navigationWrapper = this.element.querySelector(
      '.navigation-wrapper',
    );
    this.ui.$navigation = this.element.querySelector('.navigation');
    this.ui.$featuredNavigationItem = this.element.querySelector(
      '.navigation__featuredItem',
    );
    this.ui.$activeItem = this.element.querySelector(
      '[data-ref="active-item"]',
    );
    this.ui.$list = this.element.querySelector('[data-ref="list"]');
    this.ui.$listItems = Array.from(this.ui.$list.querySelectorAll('li'));
    this.ui.$next = this.element.querySelector('[data-ref="next"]');
    this.ui.$prev = this.element.querySelector('[data-ref="prev"]');
    this.ui.$searchButton = this.element.querySelector('[data-ref="search"]');
    this.ui.$wrapper = this.element.querySelector('[data-ref="wrapper"]');
    this.ui.$currentItem = this.ui.$list.querySelector(
      '.navigation__list-item--active',
    );
    this.ui.$stickySubnavBtns = Array.from(
      this.element.querySelectorAll('[data-ref="sticky-subnav-btn"]'),
    );
    this.ui.$stickySubnavDropdowns = Array.from(
      this.element.querySelectorAll('[data-ref="sticky-subnav-dropdown"]'),
    );
    this.ui.$dropdownListItems = Array.from(
      this.element.querySelectorAll('.navigation__list-item--dropdown'),
    );
  }

  closeStickySubnavDropdown(menuId = null) {
    this.ui.$stickySubnavBtns.forEach((button) => {
      const controls = button.getAttribute('aria-controls');
      const shouldClose = !menuId || controls === menuId;

      if (shouldClose) {
        button.setAttribute('aria-expanded', 'false');
        button.closest('.navigation__list-item--dropdown')?.classList.remove('is-open');
      }
    });

    this.ui.$stickySubnavDropdowns.forEach((dropdown) => {
      const shouldClose = !menuId || dropdown.id === menuId;

      if (shouldClose) {
        this.detachStickySubnavDropdown(dropdown);
        dropdown.hidden = true;
        dropdown.classList.remove('navigation__dropdown--align-end');
        this.clearStickySubnavDropdownPosition(dropdown);
        this.disableStickySubnavFocus(dropdown);
      }
    });

    if (!this.ui.$stickySubnavDropdowns.some((dropdown) => !dropdown.hidden)) {
      this.element.classList.remove('is-dropdown-open');
      document.documentElement.classList.remove('is-sticky-subnav-dropdown-open');
    }
  }

  closeStickySubnavDropdowns() {
    this.closeStickySubnavDropdown();
  }

  disableStickySubnavFocus(dropdown) {
    getFocusableElements(dropdown).forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
  }

  enableStickySubnavFocus(dropdown) {
    getFocusableElements(dropdown).forEach((element) => {
      element.removeAttribute('tabindex');
    });
  }

  getOpenStickySubnavDropdown() {
    return this.ui.$stickySubnavDropdowns.find((dropdown) => !dropdown.hidden);
  }

  handleDomLoaded() {
    if (this.isStickyNav) {
      if (this.ui.$featuredNavigationItem) {
        this.handleHorizontalScroll();
      }

      requestAnimationFrame(() => {
        this.updateStickySubnavItemVisibility();
      });
    }

    this.ui.$currentItem?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  handleClickNext() {
    this.nextItem?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  handleClickPrev() {
    this.previousItem?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }

  handleOpenSearch() {
    const event = new Event('showmodal');
    this.externalUi.$searchModal?.dispatchEvent(event);
  }

  handleHorizontalScroll() {
    const navRightEdge = this.ui.$navigation.getBoundingClientRect().right;
    const featuredRightEdge =
      this.ui.$featuredNavigationItem.getBoundingClientRect().right;
    if (featuredRightEdge - 1 <= navRightEdge) {
      this.ui.$navigationWrapper.style.setProperty('--afterFadeOpacity', '0');
    } else {
      this.ui.$navigationWrapper.style.setProperty('--afterFadeOpacity', '0.5');
    }
  }

  handleStickySubnavNavigationScroll() {
    this.updateStickySubnavItemVisibility();

    if (this.ui.$featuredNavigationItem) {
      this.handleHorizontalScroll();
    }

    const openDropdown = this.getOpenStickySubnavDropdown();

    if (openDropdown) {
      this.positionStickySubnavDropdown(openDropdown);
    }
  }

  initialize() {
    this.bindUI();
    this.setVars();
    this.bindExternalUI();
    this.bindIntersectionObservers();
    this.bindEvents();
    this.scrollToActiveItem();

    if (this.isStickyNav && this.ui.$featuredNavigationItem) {
      this.handleHorizontalScroll();
    }
  }

  isListItemFullyVisible($listItem) {
    return $listItem?.dataset.isFullyVisible !== 'false';
  }

  onStickySubnavClick(event) {
    const trigger = event.target.closest('[data-ref="sticky-subnav-btn"]');

    if (trigger) {
      event.preventDefault();
      this.toggleStickySubnavDropdown(trigger);
      return;
    }

    const link = event.target.closest('.navigation__list-item > a');

    if (!link) {
      return;
    }

    const listItem = link.closest('.navigation__list-item');

    if (!this.isListItemFullyVisible(listItem)) {
      event.preventDefault();
    }
  }

  onStickySubnavFocusIn(event) {
    const listItem = event.currentTarget;

    if (this.shouldBlurClippedStickySubnavFocus(listItem, event.target)) {
      event.target.blur();
    }
  }

  toggleStickySubnavDropdown(button) {
    const menuId = button.getAttribute('aria-controls');
    const isOpen = button.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeStickySubnavDropdowns();
      return true;
    }

    const listItem = button.closest('.navigation__list-item');

    if (!this.isListItemFullyVisible(listItem)) {
      return false;
    }

    this.openStickySubnavDropdown(menuId);
    return true;
  }

  onStickySubnavDocumentClick(event) {
    if (!this.element.classList.contains('is-dropdown-open')) {
      return;
    }

    const openDropdown = this.getOpenStickySubnavDropdown();

    if (openDropdown?.contains(event.target)) {
      return;
    }

    if (event.target.closest('[data-ref="sticky-subnav-btn"]')) {
      return;
    }

    this.closeStickySubnavDropdowns();
  }

  onStickySubnavKeydown(event) {
    if (event.key === 'Escape') {
      this.closeStickySubnavDropdowns();
      return;
    }

    const trigger = event.target.closest('[data-ref="sticky-subnav-btn"]');

    if (
      trigger &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      this.toggleStickySubnavDropdown(trigger);
    }
  }

  onStickySubnavResize() {
    this.updateStickySubnavItemVisibility();

    if (this.ui.$featuredNavigationItem) {
      this.handleHorizontalScroll();
    }

    const openDropdown = this.getOpenStickySubnavDropdown();

    if (openDropdown) {
      this.positionStickySubnavDropdown(openDropdown);
    }
  }

  onStickySubnavMouseLeave(event) {
    if (!this.getOpenStickySubnavDropdown()) {
      return;
    }

    if (event.relatedTarget && this.element.contains(event.relatedTarget)) {
      return;
    }

    this.closeStickySubnavDropdowns();
  }

  attachStickySubnavDropdown(dropdown) {
    if (dropdown.parentElement === this.element) {
      return;
    }

    this.stickySubnavDropdownHomes.set(dropdown, {
      parent: dropdown.parentElement,
      nextSibling: dropdown.nextSibling,
    });
    this.element.appendChild(dropdown);
  }

  detachStickySubnavDropdown(dropdown) {
    const home = this.stickySubnavDropdownHomes.get(dropdown);

    if (!home) {
      return;
    }

    home.parent.insertBefore(dropdown, home.nextSibling);
    this.stickySubnavDropdownHomes.delete(dropdown);
  }

  openStickySubnavDropdown(menuId) {
    if (!menuId) {
      return;
    }

    this.closeStickySubnavDropdowns();

    const button = this.ui.$stickySubnavBtns.find(
      (element) => element.getAttribute('aria-controls') === menuId,
    );
    const dropdown = this.ui.$stickySubnavDropdowns.find(
      (element) => element.id === menuId,
    );

    if (!button || !dropdown) {
      return;
    }

    const listItem = button.closest('.navigation__list-item');

    if (!this.isListItemFullyVisible(listItem)) {
      return;
    }

    button.setAttribute('aria-expanded', 'true');
    button.closest('.navigation__list-item--dropdown')?.classList.add('is-open');
    dropdown.hidden = false;
    this.attachStickySubnavDropdown(dropdown);
    this.element.classList.add('is-dropdown-open');
    document.documentElement.classList.add('is-sticky-subnav-dropdown-open');
    this.enableStickySubnavFocus(dropdown);
    this.positionStickySubnavDropdown(dropdown);
  }

  scrollToActiveItem() {
    if (!this.ui.$activeItem) {
      return;
    }

    this.ui.$activeItem.scrollIntoView({
      block: 'nearest',
      inline: 'center',
    });
  }

  setVars() {
    this.modalId = this.element.dataset.modalId;
    this.isStickyNav = this.element.classList.contains(
      'navigationBar--stickyNav',
    );
  }

  positionStickySubnavDropdown(dropdown) {
    const button = this.ui.$stickySubnavBtns.find(
      (element) => element.getAttribute('aria-controls') === dropdown.id,
    );
    const listItem = button?.closest('.navigation__list-item--dropdown');

    if (!button || !listItem) {
      return;
    }

    const barRect = this.element.getBoundingClientRect();
    const listItemRect = listItem.getBoundingClientRect();
    const dropdownWidth = dropdown.offsetWidth;
    const viewportRight = document.documentElement.clientWidth;

    dropdown.style.position = 'absolute';
    dropdown.style.top = `${listItemRect.bottom - barRect.top}px`;
    dropdown.classList.remove('navigation__dropdown--align-end');

    if (listItemRect.left + dropdownWidth > viewportRight) {
      dropdown.style.left = 'auto';
      dropdown.style.right = `${barRect.right - listItemRect.right}px`;
      dropdown.classList.add('navigation__dropdown--align-end');
    } else {
      dropdown.style.left = `${listItemRect.left - barRect.left}px`;
      dropdown.style.right = 'auto';
    }
  }

  clearStickySubnavDropdownPosition(dropdown) {
    dropdown.style.position = '';
    dropdown.style.top = '';
    dropdown.style.left = '';
    dropdown.style.right = '';
  }

  updateUI() {
    const isOverflowedLeft =
      this.ui.$listItems[0]?.dataset.isVisible === 'false';
    const isOverflowedRight =
      this.ui.$listItems[this.ui.$listItems.length - 1]?.dataset.isVisible ===
      'false';

    this.element.dataset.isOverflowedLeft = isOverflowedLeft;
    this.element.dataset.isOverflowedRight = isOverflowedRight;
  }
}

registerModule('NavigationBar', NavigationBar);
